import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import { infiniteQueryOptions } from "react-query";

export const gamificationKey = createQueryKeyFactory("gamification");

export interface GamificationReward {
  awarded_by?: {
    name: string;
    avatar_url: string;
  };
  event_name:
    | "gamification.member.points_awarded"
    | "gamification.member.points_revoked";
  id: number;
  points: number;
  reason: string | null;
  revoked: boolean;
  triggered_at: string;
}

export interface GamificationRewardsPage<
  TReward extends GamificationReward = GamificationReward,
> {
  has_next_page: boolean;
  page: number;
  records: TReward[];
}

export interface GamificationMember {
  community_member_id: number;
  current_level: number;
  current_level_name: string;
  level_progress: number;
  points_to_next_level: number;
  total_points: number;
}

export interface CreatedPointAward {
  community_member_id: number;
  created_at: string;
  id: number;
  points: number;
  reason: string | null;
  revoked: boolean;
}

interface GamificationRewardsQueryData {
  pageParams: unknown[];
  pages: GamificationRewardsPage[];
}

export const addRewardToGamificationCache = (reward: CreatedPointAward) => {
  const queryKey = gamificationKey.key(
    "rewards",
    "list",
    reward.community_member_id,
  );
  const cachedReward: GamificationReward = {
    ...reward,
    event_name: "gamification.member.points_awarded",
    triggered_at: reward.created_at,
  };

  queryClient.setQueryData<GamificationRewardsQueryData>(
    queryKey,
    currentData => {
      const pages = currentData?.pages ?? [
        { has_next_page: false, page: 1, records: [] },
      ];
      const [firstPage, ...remainingPages] = pages;

      if (
        pages.some(page =>
          page.records.some(record => record.id === cachedReward.id),
        )
      ) {
        return currentData;
      }

      return {
        pageParams: currentData?.pageParams ?? [1],
        pages: [
          { ...firstPage, records: [cachedReward, ...firstPage.records] },
          ...remainingPages,
        ],
      };
    },
  );
};

export const gamificationQueries = {
  memberScores: ({
    memberIds,
    enabled = true,
  }: {
    memberIds?: (number | string)[];
    enabled?: boolean;
  }) => ({
    queryKey: gamificationKey.key("members", memberIds ?? []),
    queryFn: () =>
      reactQueryGet<GamificationMember[]>(
        internalApi.gamification.members({
          params: { member_ids: memberIds },
        }),
      ),
    gcTime: 60 * 1000,
    staleTime: 60 * 1000,
    enabled,
  }),
  rewardsList: <TReward extends GamificationReward = GamificationReward>(
    memberId: number,
  ) =>
    infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: gamificationKey.key("rewards", "list", memberId),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<GamificationRewardsPage<TReward>>(
          internalApi.gamification.pointAwards({
            params: { member_id: memberId, page: pageParam },
          }),
        ),
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    }),
};

export const gamificationInvalidations = {
  all: () =>
    queryClient.invalidateQueries({ queryKey: gamificationKey.root() }),
};
